import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LookupEndpoints } from '@core/constants/endpoints.const';
import { ApiResponse } from '@models/response.model';
import { ILookupModel } from '@models/master/master.model';
import { Observable } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';
import { CurrentUserService } from '@services/common/currentuser.service';
import { UserType } from '@core/enums/type.enum';

export interface ILookupRequest {
  entityName: string;
  searchText?: string;
  extraParams?: Record<string, any>;
  fields?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LookupService {
  private inFlightRequests = new Map<string, Observable<ApiResponse<Array<ILookupModel>>>>();

  constructor(
    private httpClient: HttpClient,
    private currentUserService: CurrentUserService
  ) {}

  getLookup(request: ILookupRequest): Observable<ApiResponse<Array<ILookupModel>>> {
    const currentUser = this.currentUserService.get();
    let extraParams = request.extraParams ? { ...request.extraParams } : {};

    if (currentUser) {
      if (currentUser.userTypeId === UserType.Admin || currentUser.userTypeId === UserType.Receptionist) {
        if (request.entityName === 'Practitioner') {
          if (currentUser.userLocations) {
            // Only inject user locations if the component hasn't explicitly requested a specific LocationId
            if (!extraParams['LocationIds']) {
              extraParams = { 
                ...extraParams, 
                LocationIds: currentUser.userLocations.map(ul => ul.locationIdEnc).join(',') 
              };
            }
          }
        }
      }
    }

    let params = new HttpParams();
    if (request.searchText) {
      params = params.set('searchText', request.searchText);
    }
    if (Object.keys(extraParams).length > 0) {
      params = params.set('extraParams', JSON.stringify(extraParams));
    }
    if (request.fields) {
      params = params.set('fields', request.fields);
    }

    const cacheKey = `${request.entityName}?${params.toString()}`;
    const inFlight = this.inFlightRequests.get(cacheKey);
    if (inFlight) {
      return inFlight;
    }

    const request$ = this.httpClient.get<ApiResponse<Array<ILookupModel>>>(
      `${LookupEndpoints.Lookup}/${request.entityName}`,
      { params }
    ).pipe(
      finalize(() => this.inFlightRequests.delete(cacheKey)),
      shareReplay(1)
    );

    this.inFlightRequests.set(cacheKey, request$);
    return request$;
  }
}